/**
 * SendMediaNode
 *
 * Sends one OR many media files (image / video / document / audio) in a
 * single flow step. Two authoring modes — both can produce a single OR
 * multiple sends, so the user never has to switch nodes:
 *
 *   1) "manual"   — author picks each item explicitly (type + URL/upload + caption/fileName).
 *                   The list supports any number of rows (no hard cap).
 *   2) "variable" — author points to ONE variable that resolves at runtime to:
 *                     - a single object  { type, url, caption?, filename? }
 *                     - an array of those objects
 *                   (Use this when the list of attachments is dynamic — e.g.
 *                    a webhook returns an array of invoice URLs.)
 *
 * The handler on wabaserver normalises both modes into a single items[] list
 * and sends one WhatsApp message per item. See SendMediaNodeHandler.js.
 *
 * Shape of node.data:
 *   {
 *     label: "Send Media",
 *     mode: "manual" | "variable",        // default "manual"
 *     items: [
 *       { id, type, mediaUrl, caption, fileName }
 *     ],
 *     variableName: ""
 *   }
 */
import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil,
  Check,
  X,
  Plus,
  Trash2,
  Upload,
  Loader,
  Image as ImageIcon,
  Video as VideoIcon,
  File as FileIcon,
  Music as AudioIcon,
  Paperclip,
  Variable,
  AlertTriangle,
} from "lucide-react";
import { uploadDocumentFile } from "../../apis/documents";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import { getVariableNamesFromText } from "../../utils/flowVariableValidation";

// ─── Constants ────────────────────────────────────────────────

// WhatsApp Cloud API media spec, mirrored client-side so the author hits a
// clear error before we burn a network round-trip on a guaranteed-to-fail
// upload (or, worse, ship the message to Meta and have it rejected).
//
//   Image     5 MB    JPG, PNG
//   Video    16 MB    MP4 (H.264 + AAC)
//   Audio    16 MB    MP3, AAC, AMR, OGG
//   Document 100 MB   PDF, DOCX, XLSX, PPTX, TXT
const MEDIA_TYPES = [
  {
    value: "image",
    label: "Image",
    Icon: ImageIcon,
    accept: "image/jpeg,image/png,.jpg,.jpeg,.png",
    mimeTypes: ["image/jpeg", "image/png"],
    extensions: ["jpg", "jpeg", "png"],
    maxBytes: 5 * 1024 * 1024,
    formatsLabel: "JPG, PNG",
    sizeLabel: "5 MB",
  },
  {
    value: "video",
    label: "Video",
    Icon: VideoIcon,
    accept: "video/mp4,.mp4",
    mimeTypes: ["video/mp4"],
    extensions: ["mp4"],
    maxBytes: 16 * 1024 * 1024,
    formatsLabel: "MP4 (H.264 + AAC)",
    sizeLabel: "16 MB",
  },
  {
    value: "document",
    label: "Document",
    Icon: FileIcon,
    accept:
      "application/pdf," +
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
      "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
      "text/plain,.pdf,.docx,.xlsx,.pptx,.txt",
    mimeTypes: [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
    ],
    extensions: ["pdf", "docx", "xlsx", "pptx", "txt"],
    maxBytes: 100 * 1024 * 1024,
    formatsLabel: "PDF, DOCX, XLSX, PPTX, TXT",
    sizeLabel: "100 MB",
  },
  {
    value: "audio",
    label: "Audio",
    // Some browsers report unusual MIME types for AMR / OGG, so we list both
    // MIME and extensions in `accept`. The validator below also accepts either.
    accept: "audio/mpeg,audio/aac,audio/ogg,audio/amr,.mp3,.aac,.ogg,.amr",
    Icon: AudioIcon,
    mimeTypes: ["audio/mpeg", "audio/aac", "audio/ogg", "audio/amr"],
    extensions: ["mp3", "aac", "ogg", "amr"],
    maxBytes: 16 * 1024 * 1024,
    formatsLabel: "MP3, AAC, AMR, OGG",
    sizeLabel: "16 MB",
  },
];

const TYPE_BY_VALUE = MEDIA_TYPES.reduce((acc, t) => {
  acc[t.value] = t;
  return acc;
}, {});

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

/**
 * Validate a picked file against the WhatsApp spec for the given media type.
 * Returns null if OK, otherwise a short human-readable error string.
 */
const validateMediaFile = (file, typeMeta) => {
  if (!file || !typeMeta) return "No file selected.";
  if (file.size > typeMeta.maxBytes) {
    return `File is ${formatBytes(file.size)} — ${typeMeta.label} must be ≤ ${typeMeta.sizeLabel}.`;
  }
  const mimeOk = typeMeta.mimeTypes.includes(file.type);
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const extOk = typeMeta.extensions.includes(ext);
  if (!mimeOk && !extOk) {
    return `Unsupported format. Allowed: ${typeMeta.formatsLabel}.`;
  }
  return null;
};

const VARIABLE_EXAMPLE = `[
  { "type": "document", "url": "https://.../invoice.pdf", "filename": "invoice.pdf" },
  { "type": "image",    "url": "https://.../photo.jpg",   "caption": "Order photo" }
]`;

// ─── Helpers ──────────────────────────────────────────────────
const newItemId = () => `med_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const makeBlankItem = (type = "image") => ({
  id: newItemId(),
  type,
  mediaUrl: "",
  caption: "",
  fileName: "",
});

/** A short, friendly summary of an item for the collapsed card. */
const summariseItem = (item) => {
  if (!item) return "";
  if (item.mediaUrl && item.mediaUrl.includes("{{")) {
    const vars = getVariableNamesFromText(item.mediaUrl);
    return vars.length ? `From {{${vars.join(", ")}}}` : "From variable";
  }
  if (item.type === "document") return item.fileName || "Document";
  return TYPE_BY_VALUE[item.type]?.label || "Media";
};

// ─── Collapsed View ───────────────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const mode = data.mode || "manual";
  const items = data.items || [];

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#FFF7ED", padding: "10px", minWidth: "240px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit media"
      >
        <Pencil size={12} className="text-amber-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        {/* Title row */}
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-amber-100 rounded-lg">
            <Paperclip size={14} className="text-amber-600" />
          </div>
          <span className="text-[12px] font-bold text-amber-900">Send Media</span>
          <span className="ml-auto text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
            {mode === "variable" ? "From variable" : "Manual"}
          </span>
        </div>

        {/* Body */}
        {mode === "variable" ? (
          data.variableName ? (
            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-amber-50 rounded text-[10px] text-amber-800">
              <Variable size={10} className="flex-shrink-0" />
              <span className="font-mono font-semibold truncate">{`{{${data.variableName}}}`}</span>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 italic">No variable selected</p>
          )
        ) : items.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic">No media added</p>
        ) : (
          <div className="space-y-1">
            {items.slice(0, 3).map((item) => {
              const Icon = TYPE_BY_VALUE[item.type]?.Icon || FileIcon;
              return (
                <div key={item.id} className="flex items-center gap-1.5 text-[10px] text-gray-700">
                  <Icon size={11} className="text-amber-600 flex-shrink-0" />
                  <span className="truncate">{summariseItem(item)}</span>
                </div>
              );
            })}
            {items.length > 3 && (
              <p className="text-[10px] text-amber-700 font-semibold">
                +{items.length - 3} more
              </p>
            )}
          </div>
        )}

        {/* Footer count */}
        {mode === "manual" && items.length > 0 && (
          <p className="text-[9px] text-gray-400 mt-1.5">
            {items.length === 1 ? "1 attachment" : `${items.length} attachments`}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Single Manual Item Row ──────────────────────────────────
const ItemRow = ({ index, item, onChange, onRemove, flowVariables, onCreateVariable }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const urlInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const typeMeta = TYPE_BY_VALUE[item.type] || TYPE_BY_VALUE.image;
  const Icon = typeMeta.Icon;

  const update = (field, value) => onChange({ ...item, [field]: value });

  const insertVarInUrl = (varName) => {
    if (!varName) return;
    const cur = item.mediaUrl || "";
    const input = urlInputRef.current;
    const pos = input?.selectionStart ?? cur.length;
    update("mediaUrl", cur.slice(0, pos) + `{{${varName}}}` + cur.slice(pos));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    setUploadError("");
    if (!file) return;

    const validationErr = validateMediaFile(file, typeMeta);
    if (validationErr) {
      setUploadError(validationErr);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const response = await uploadDocumentFile(file);
      const mediaUrl = response.data.data.doc_url;
      onChange({ ...item, mediaUrl, fileName: file.name });
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadError("Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isVariableUrl = item.mediaUrl && item.mediaUrl.includes("{{");

  return (
    <div className="bg-white rounded-lg border border-amber-100 p-2 space-y-1.5">
      {/* Row header: index + type select + remove */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
          #{index + 1}
        </span>
        <Icon size={12} className="text-amber-600 flex-shrink-0" />
        <select
          value={item.type}
          onChange={(e) => update("type", e.target.value)}
          className="flex-1 text-[11px] font-semibold text-gray-800 bg-amber-50 px-2 py-1 rounded border-0 focus:outline-none focus:ring-1 focus:ring-amber-400 cursor-pointer"
        >
          {MEDIA_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
          title="Remove item"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {/* URL + variable + upload */}
      <div className="flex items-center gap-1">
        <input
          ref={urlInputRef}
          type="text"
          value={item.mediaUrl}
          onChange={(e) => update("mediaUrl", e.target.value)}
          placeholder="https://... or {{variable}}"
          className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-gray-400"
        />
        <VariableDropdown
          value=""
          onChange={(name) => { if (name) insertVarInUrl(name); }}
          flowVariables={flowVariables || []}
          allowCreate
          onCreateVariable={onCreateVariable}
          placeholder="Var"
          className="w-20"
        />
      </div>

      {/* Upload button + WhatsApp spec hint */}
        <label className="block cursor-pointer">
          <div
            className={`rounded-lg p-1.5 text-center transition-all ${
              uploading
                ? "bg-amber-50 border border-amber-300"
                : "bg-[#F7F7F7] hover:bg-[#EEE] border border-transparent"
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-1.5">
                <Loader size={12} className="text-amber-600 animate-spin" />
                <span className="text-[10px] text-amber-700 font-semibold">Uploading…</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-0.5">
                <div className="flex items-center justify-center gap-1.5">
                  <Upload size={12} className="text-gray-500" />
                  <span className="text-[10px] font-medium text-gray-600">
                    Upload {typeMeta.label.toLowerCase()}
                  </span>
                </div>
                <span className="text-[9px] text-gray-400">
                  {typeMeta.formatsLabel} · ≤ {typeMeta.sizeLabel}
                </span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={typeMeta.accept}
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
        {uploadError && (
          <p className="text-[10px] text-red-600 font-medium">{uploadError}</p>
        )}

      {/* Caption — hidden for audio (WhatsApp doesn't support it) */}
      {item.type !== "audio" && (
        <input
          type="text"
          value={item.caption}
          onChange={(e) => update("caption", e.target.value)}
          placeholder="Caption (optional)"
          maxLength={1024}
          className="w-full px-2 py-1.5 text-[11px] bg-[#F7F7F7] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-gray-400"
        />
      )}

      {/* File name — only meaningful for documents */}
      {item.type === "document" && (
        <input
          type="text"
          value={item.fileName}
          onChange={(e) => update("fileName", e.target.value)}
          placeholder="File name (e.g. invoice.pdf)"
          className="w-full px-2 py-1.5 text-[11px] bg-[#F7F7F7] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-gray-400 font-mono"
        />
      )}

      {/* Variable hint */}
      {isVariableUrl && (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
          <Variable size={9} className="flex-shrink-0" />
          <span>URL resolved at send time.</span>
        </div>
      )}
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => ({
    mode: data.mode || "manual",
    items: (data.items && data.items.length > 0)
      ? data.items.map((it) => ({
          id: it.id || newItemId(),
          type: it.type || "image",
          mediaUrl: it.mediaUrl || "",
          caption: it.caption || "",
          fileName: it.fileName || "",
        }))
      : [makeBlankItem()],
    variableName: data.variableName || "",
  }));

  const setMode = (mode) => setForm((p) => ({ ...p, mode }));

  const updateItem = (idx, next) => {
    setForm((p) => ({
      ...p,
      items: p.items.map((it, i) => (i === idx ? next : it)),
    }));
  };

  const removeItem = (idx) => {
    setForm((p) => ({
      ...p,
      items: p.items.filter((_, i) => i !== idx),
    }));
  };

  const addItem = () => {
    setForm((p) => ({ ...p, items: [...p.items, makeBlankItem()] }));
  };

  const handleSave = () => {
    if (form.mode === "manual") {
      onSave({
        mode: "manual",
        items: form.items.map((it) => ({ ...it })),
        variableName: form.variableName,
      });
    } else {
      onSave({
        mode: "variable",
        items: form.items,
        variableName: (form.variableName || "").trim(),
      });
    }
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#FFF7ED", borderRadius: "12px", padding: "14px", minWidth: "380px", maxWidth: "440px" }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-200 rounded-lg">
            <Paperclip size={14} className="text-amber-700" />
          </div>
          <span className="text-[13px] font-bold text-amber-900">Send Media</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Mode switch — first thing the user sees */}
      <NodeEditorTabs nodeType="sendMedia">      <div className="bg-white rounded-lg p-1 flex gap-1 mb-3 border border-amber-100">
        <button
          onClick={() => setMode("manual")}
          className={`flex-1 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
            form.mode === "manual"
              ? "bg-amber-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-amber-50"
          }`}
        >
          Manual
        </button>
        <button
          onClick={() => setMode("variable")}
          className={`flex-1 py-1.5 text-[11px] font-bold rounded-md transition-colors ${
            form.mode === "variable"
              ? "bg-amber-600 text-white shadow-sm"
              : "text-gray-600 hover:bg-amber-50"
          }`}
        >
          From variable
        </button>
      </div>

      {/* ── Manual mode panel ── */}
      {form.mode === "manual" && (
        <div className="space-y-2 mb-3">
          {form.items.map((item, idx) => (
            <ItemRow
              key={item.id}
              index={idx}
              item={item}
              onChange={(next) => updateItem(idx, next)}
              onRemove={() => removeItem(idx)}
              flowVariables={flowVariables}
              onCreateVariable={onCreateVariable}
            />
          ))}

          {form.items.length === 0 && (
            <div className="bg-white rounded-lg p-3 text-center border border-dashed border-amber-200">
              <p className="text-[11px] text-gray-500">No media yet. Click "Add media" to get started.</p>
            </div>
          )}

          <button
            onClick={addItem}
            className="w-full py-1.5 text-[11px] font-bold text-amber-700 border border-dashed border-amber-300 rounded-lg hover:bg-amber-50 transition-colors flex items-center justify-center gap-1"
          >
            <Plus size={12} />
            Add media{form.items.length > 0 ? ` (${form.items.length})` : ""}
          </button>
        </div>
      )}

      {/* ── Variable mode panel ── */}
      {form.mode === "variable" && (
        <div className="space-y-2 mb-3">
          <div className="bg-white rounded-lg p-3 border border-amber-100 space-y-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider block">
              Media Variable
            </label>
            <VariableDropdown
              value={form.variableName}
              onChange={(name) => setForm((p) => ({ ...p, variableName: name || "" }))}
              flowVariables={flowVariables || []}
              allowCreate
              onCreateVariable={onCreateVariable}
              placeholder="Pick a variable…"
            />
            <p className="text-[10px] text-gray-500 leading-snug">
              The variable should resolve to a single object or an array of objects.
              Each object needs <span className="font-mono font-semibold">type</span> and{" "}
              <span className="font-mono font-semibold">url</span>.{" "}
              Optional: <span className="font-mono">caption</span>,{" "}
              <span className="font-mono">filename</span>.
            </p>
          </div>

          {/* Example block */}
          <div className="bg-amber-50 rounded-lg p-2 border border-amber-100">
            <div className="flex items-center gap-1 mb-1">
              <Variable size={10} className="text-amber-700" />
              <span className="text-[9px] font-bold text-amber-800 uppercase tracking-wider">
                Example value
              </span>
            </div>
            <pre className="text-[9px] font-mono text-amber-900 whitespace-pre-wrap leading-tight">
              {VARIABLE_EXAMPLE}
            </pre>
          </div>

          {!form.variableName && (
            <div className="flex items-start gap-1 px-2 py-1 bg-amber-50 rounded text-[10px] text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>Pick a variable that contains the media list.</span>
            </div>
          )}
        </div>
      )}

      {/* Bottom save / cancel */}
      </NodeEditorTabs>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main Node ────────────────────────────────────────────────
const SendMediaNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("sendMedia");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div
      className={`relative transition-all shadow-md`}
      style={{ borderRadius: "12px" }}
    >
      <NodeActionButtons id={id} data={data} />

      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-amber-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-amber-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default SendMediaNode;
